import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Avatar } from "../ui/Avatar";
import { useNamedPeople, useUpcomingBirthdays, useUpcomingYahrzeits, type UpcomingDate } from "../data";
import { IcCalendar, IcChevron } from "../ui/icons";

/**
 * Personal dates, looking forward.
 *
 * Built to answer one question: **who has a birthday next month?** Until this
 * screen existed the only way to see somebody's date was to open their card,
 * which is the wrong way round — the entire value of holding a birthday is
 * knowing it is coming before anybody reminds you.
 *
 * So the axis is the date, not the person. Everything is grouped by month and
 * ordered, birthdays and yahrzeits side by side, because the question a rabbi
 * asks on a Sunday morning is "what is coming up", not "what is coming up of
 * this particular type".
 *
 * A yahrzeit is shown differently from a birthday on purpose. A birthday is a
 * greeting; a yahrzeit is a family to call, and the name on the card is the
 * deceased while the person to reach is the contact. Rendering them the same way
 * would invite exactly the wrong message.
 */

const WINDOWS = [
  { id: "30", label: "30 יום", days: 30 },
  { id: "60", label: "חודשיים", days: 60 },
  { id: "180", label: "חצי שנה", days: 180 },
] as const;

function isoOffset(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function monthKey(iso: string): string {
  return iso.slice(0, 7);
}

function monthLabel(key: string): string {
  const [y, m] = key.split("-").map(Number);
  return new Intl.DateTimeFormat("he-IL", { month: "long", year: "numeric" }).format(
    new Date(Date.UTC(y!, m! - 1, 1)),
  );
}

function dayLabel(iso: string): string {
  return new Intl.DateTimeFormat("he-IL", { weekday: "short", day: "numeric", month: "short" }).format(
    new Date(`${iso}T12:00:00Z`),
  );
}

/** Days from today, so "in 3 days" can be said rather than only a date. */
function daysAway(iso: string): number {
  const today = new Date().toISOString().slice(0, 10);
  return Math.round(
    (new Date(`${iso}T12:00:00Z`).getTime() - new Date(`${today}T12:00:00Z`).getTime()) / 86400000,
  );
}

function relativeLabel(iso: string): string {
  const n = daysAway(iso);
  if (n <= 0) return "היום";
  if (n === 1) return "מחר";
  if (n <= 7) return `בעוד ${n} ימים`;
  return "";
}

export function DatesPage() {
  const [windowId, setWindowId] = useState<(typeof WINDOWS)[number]["id"]>("30");
  const days = WINDOWS.find((w) => w.id === windowId)!.days;

  const from = isoOffset(0);
  const to = isoOffset(days);

  const { data: people, loading: loadingPeople } = useUpcomingBirthdays(from, to);
  const { data: memorials, loading: loadingMemorials } = useUpcomingYahrzeits(from, to);

  const contactIds = useMemo(
    () =>
      memorials
        .map((m) => m.contactPersonId)
        .filter((id): id is string => typeof id === "string" && id.trim() !== ""),
    [memorials],
  );
  const { data: contactPeople } = useNamedPeople(contactIds);
  const contactNameById = useMemo(
    () => new Map(contactPeople.map((p) => [p.id, (p.displayName as string) ?? ""])),
    [contactPeople],
  );

  const items: UpcomingDate[] = useMemo(() => {
    const out: UpcomingDate[] = [];

    for (const p of people) {
      if (!p.nextBirthdayAt) continue;
      out.push({
        id: `bd_${p.id}`,
        kind: "birthday",
        date: p.nextBirthdayAt as string,
        personId: p.id,
        personName: (p.displayName as string) ?? "בלי שם",
        subjectName: null,
        hebrewLabel: hebrewLabelOf(p.hebrewBirthDate ?? p.hebrewBirthday),
      });
    }

    for (const m of memorials) {
      if (!m.nextOccurrenceAt) continue;
      const contactId = m.contactPersonId ?? null;
      out.push({
        id: `yz_${m.id}`,
        kind: "yahrzeit",
        date: m.nextOccurrenceAt,
        personId: contactId,
        // The person to reach is the family contact; the name below is whose
        // memory it is.
        personName: (contactId && contactNameById.get(contactId)) || "",
        subjectName: m.deceasedDisplayName ?? "בלי שם",
        hebrewLabel: hebrewLabelOf(m.dateOfDeathHebrew),
      });
    }

    return out.sort((a, b) => a.date.localeCompare(b.date));
  }, [people, memorials, contactNameById]);

  const months = useMemo(() => {
    const m = new Map<string, UpcomingDate[]>();
    for (const it of items) {
      const k = monthKey(it.date);
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(it);
    }
    return [...m.entries()];
  }, [items]);

  const loading = loadingPeople || loadingMemorials;

  return (
    <div className="screen">
      <header className="hdr">
        <div className="hdr-top">
          <div className="hdr-titles">
            <h1>
              <IcCalendar size={20} /> תאריכים
            </h1>
            <p className="sub">ימי הולדת ואזכרות שמתקרבים</p>
          </div>
        </div>

        <div className="pilltabs" role="tablist">
          {WINDOWS.map((w) => (
            <button
              key={w.id}
              role="tab"
              aria-selected={windowId === w.id}
              className={windowId === w.id ? "on" : undefined}
              onClick={() => setWindowId(w.id)}
            >
              {w.label}
            </button>
          ))}
        </div>
      </header>

      <div className="body">
        {loading ? (
          <p className="muted" style={{ padding: 22, textAlign: "center" }}>
            טוען…
          </p>
        ) : items.length === 0 ? (
          <div className="empty">
            <div className="empty-ico tint-orange">
              <IcCalendar size={26} />
            </div>
            <h3>אין תאריכים בטווח הזה</h3>
            <p>
              ימי הולדת ואזכרות מגיעים מהלוח של בית הכנסת ומהטפסים. אם רק עכשיו חיברתם אותם,
              ייתכן שהבדיקה הראשונה עוד לא רצה. אפשר להריץ אותה מההגדרות.
            </p>
          </div>
        ) : (
          months.map(([key, group]) => (
            <section className="section" key={key}>
              <div className="section-head">
                <div className="section-title">
                  {monthLabel(key)} <span className="section-n">{group.length}</span>
                </div>
              </div>

              {group.map((it) => (
                <DateRow key={it.id} item={it} />
              ))}
            </section>
          ))
        )}
      </div>
    </div>
  );
}

/** Renders a stored Hebrew day/month pair, whichever shape it came in. */
function hebrewLabelOf(v: unknown): string | null {
  if (!v || typeof v !== "object") return null;
  const o = v as { dayRaw?: string | null; monthRaw?: string | null };
  if (o.dayRaw && o.monthRaw) return `${o.dayRaw} ${o.monthRaw}`;
  return null;
}

function DateRow({ item }: { item: UpcomingDate }) {
  const relative = relativeLabel(item.date);
  const isBirthday = item.kind === "birthday";

  const body = (
    <article className="card">
      <div className="card-body row">
        {isBirthday ? (
          <Avatar name={item.personName} size={44} />
        ) : (
          <div
            className="avatar avatar-lg tint-slate"
            aria-hidden="true"
          >
            נ
          </div>
        )}

        <div className="grow">
          {isBirthday ? (
            <>
              <p className="name">{item.personName}</p>
              <p className="muted">יום הולדת</p>
            </>
          ) : (
            <>
              <p className="name">
                אזכרה — {item.subjectName}
              </p>
              <p className="muted">
                {item.personName
                  ? `להתקשר ל${item.personName}`
                  : item.personId
                    ? "להתקשר למשפחה"
                    : "אין איש קשר מקושר"}
              </p>
            </>
          )}
          <p className="faint">
            {dayLabel(item.date)}
            {item.hebrewLabel ? ` · ${item.hebrewLabel}` : ""}
          </p>
        </div>

        {relative && <span className="date-soon">{relative}</span>}
        {item.personId && <IcChevron size={16} />}
      </div>
    </article>
  );

  return item.personId ? <Link to={`/people/${item.personId}`}>{body}</Link> : body;
}
